'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { io } from 'socket.io-client';
import { usePathname, useSearchParams } from 'next/navigation';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || '';

const getAuthToken = () => {
    if (typeof document === 'undefined') return null;
    const cookieToken = document.cookie
        .split('; ')
        .find((row) => row.startsWith('kayease_token='))
        ?.split('=')[1];
    if (cookieToken) return cookieToken;
    try {
        return sessionStorage.getItem('authToken');
    } catch {
        return null;
    }
};

const getNotificationIcon = (type) => {
    const iconMap = {
        message: '💬',
        helpdesk_message: '🆘',
        class_test: '📝',
        homework: '📚',
        assignment: '📋',
        consent_form: '📄',
        timetable_update: '📅',
        examination: '🎓',
        notice_board: '📢',
        event: '🎉',
        announcement: '📣',
        marks_published: '⭐',
        attendance: '✅',
        fee_reminder: '💰',
        fee_payment: '💰',
        library: '📖',
        hostel: '🏠',
        hostel_leave: '🏠',
        report_card: '🎓',
        transfer_certificate: '📄',
        sports_fixture: '🏅',
        sports_result: '🏅',
        sports_roster: '🏅',
        other: '🔔',
    };
    return iconMap[type] || iconMap.other;
};

const getTimeAgo = (date) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return notifDate.toLocaleDateString();
};

// Global state to track active conversation
let activeConversationUserId = null;

export const setActiveConversation = (userId) => {
    activeConversationUserId = userId;
};

export const clearActiveConversation = () => {
    activeConversationUserId = null;
};

export const getActiveConversation = () => {
    return activeConversationUserId;
};

export default function NotificationBell() {
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const dropdownRef = useRef(null);
    const socketRef = useRef(null);
    const { showToast } = useToast();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    // Get active conversation from URL (for messages page)
    const urlUserId = searchParams?.get('userId');

    // Update active conversation when URL changes
    useEffect(() => {
        if (pathname?.startsWith('/messages') && urlUserId) {
            setActiveConversation(urlUserId);
        } else if (pathname?.startsWith('/helpdesk')) {
            setActiveConversation('helpdesk');
        } else {
            clearActiveConversation();
        }

        return () => {
            // Don't clear on unmount, only when leaving messages
            if (!pathname?.startsWith('/messages') && !pathname?.startsWith('/helpdesk')) {
                clearActiveConversation();
            }
        };
    }, [pathname, urlUserId]);

    const getUserRoleFromToken = (token) => {
        try {
            if (!token) return null;
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.role?.toLowerCase();
        } catch {
            return null;
        }
    };

    // Fetch unread count
    const fetchUnreadCount = useCallback(async () => {
        const token = getAuthToken();
        if (!token) return;

        const role = getUserRoleFromToken(token);
        const canAccessHelpdesk = role === 'superadmin' || role === 'schooladmin';
        // Super admins usually don't have regular school notifications
        const canAccessRegular = role !== 'superadmin';

        try {
            const promises = [];
            // Map indices to result types to handle array destructuring correctly
            // 0: regular, 1: helpdesk

            // We use simple variables instead of array mapping for clarity
            let regularPromise = Promise.resolve({ ok: false });
            let helpdeskPromise = Promise.resolve({ ok: false });

            if (canAccessRegular) {
                regularPromise = fetch(`${API_BASE_URL}/api/notifications/unread-count`, {
                    headers: { Authorization: `Bearer ${token}` },
                }).then(res => res.ok ? res : { ok: false, status: res.status }).catch(() => ({ ok: false, status: 403 }));
            }

            if (canAccessHelpdesk) {
                helpdeskPromise = fetch(`${API_BASE_URL}/api/helpdesk/notifications/unread-count`, {
                    headers: { Authorization: `Bearer ${token}` },
                }).then(res => res.ok ? res : { ok: false }).catch(() => ({ ok: false }));
            }

            const [regularResponse, helpdeskResponse] = await Promise.all([regularPromise, helpdeskPromise]);

            let totalCount = 0;

            if (regularResponse?.ok) {
                const data = await regularResponse.json();
                totalCount += data.unreadCount || 0;
            }

            if (helpdeskResponse?.ok) {
                const data = await helpdeskResponse.json();
                totalCount += data.unreadCount || 0;
            }

            setUnreadCount(totalCount);
        } catch (error) {
            console.error('[NotificationBell] Failed to fetch unread count:', error);
        }
    }, []);

    // Fetch only unread notifications
    const fetchNotifications = useCallback(async () => {
        const token = getAuthToken();
        if (!token) return;

        const role = getUserRoleFromToken(token);
        const canAccessHelpdesk = role === 'superadmin' || role === 'schooladmin';
        const canAccessRegular = role !== 'superadmin';

        setIsLoading(true);
        try {
            let regularPromise = Promise.resolve({ ok: false });
            let helpdeskPromise = Promise.resolve({ ok: false });

            if (canAccessRegular) {
                regularPromise = fetch(`${API_BASE_URL}/api/notifications?unreadOnly=true&limit=50`, {
                    headers: { Authorization: `Bearer ${token}` },
                }).then(res => res.ok ? res : { ok: false, status: res.status }).catch(() => ({ ok: false, status: 403 }));
            }

            if (canAccessHelpdesk) {
                helpdeskPromise = fetch(`${API_BASE_URL}/api/helpdesk/notifications?unreadOnly=true&limit=50`, {
                    headers: { Authorization: `Bearer ${token}` },
                }).then(res => res.ok ? res : { ok: false }).catch(() => ({ ok: false }));
            }

            const [regularResponse, helpdeskResponse] = await Promise.all([regularPromise, helpdeskPromise]);

            let allNotifications = [];
            let totalUnreadCount = 0;

            if (regularResponse?.ok) {
                const data = await regularResponse.json();
                allNotifications = [...allNotifications, ...(data.notifications || [])];
                totalUnreadCount += data.unreadCount || 0;
            }

            if (helpdeskResponse?.ok) {
                const data = await helpdeskResponse.json();
                allNotifications = [...allNotifications, ...(data.notifications || [])];
                totalUnreadCount += data.unreadCount || 0;
            }

            // Sort by createdAt descending
            allNotifications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            setNotifications(allNotifications);
            setUnreadCount(totalUnreadCount);
        } catch (error) {
            console.error('[NotificationBell] Failed to fetch notifications:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Mark as read and remove from list
    const markAsRead = async (notificationId, notificationType) => {
        const token = getAuthToken();
        if (!token) return;

        try {
            // Determine which API endpoint to use based on notification type
            const endpoint = notificationType === 'helpdesk_message'
                ? `${API_BASE_URL}/api/helpdesk/notifications/${notificationId}/read`
                : `${API_BASE_URL}/api/notifications/${notificationId}/read`;

            const response = await fetch(endpoint, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.ok) {
                // Remove from list immediately
                setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
                setUnreadCount((prev) => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('[NotificationBell] Failed to mark as read:', error);
        }
    };

    // Mark all as read
    const markAllAsRead = async () => {
        const token = getAuthToken();
        if (!token) return;

        try {
            // Mark both regular and helpdesk notifications as read
            await Promise.all([
                fetch(`${API_BASE_URL}/api/notifications/mark-all-read`, {
                    method: 'PATCH',
                    headers: { Authorization: `Bearer ${token}` },
                }).catch(() => null),
                fetch(`${API_BASE_URL}/api/helpdesk/notifications/mark-all-read`, {
                    method: 'PATCH',
                    headers: { Authorization: `Bearer ${token}` },
                }).catch(() => null),
            ]);

            setNotifications([]);
            setUnreadCount(0);
            showToast({ severity: 'success', message: 'All notifications marked as read' });
        } catch (error) {
            console.error('[NotificationBell] Failed to mark all as read:', error);
            showToast({ severity: 'error', message: 'Failed to mark all as read' });
        }
    };

    // Handle notification click - mark as read and navigate
    const handleNotificationClick = (notification) => {
        markAsRead(notification._id, notification.type);
        if (notification.actionUrl) {
            setIsOpen(false);
            window.location.href = notification.actionUrl;
        }
    };

    // Initialize Socket.IO connection
    // FCM web push: register this browser so notifications still arrive
    // after the tab is closed. Env-gated no-op without the
    // NEXT_PUBLIC_FIREBASE_* vars; superadmins don't receive school
    // notifications, so their browsers are never registered. Delayed a
    // beat so the permission prompt doesn't collide with page load.
    useEffect(() => {
        const token = getAuthToken();
        if (!token) return;
        if (getUserRoleFromToken(token) === 'superadmin') return;
        const timer = setTimeout(() => {
            import('@/lib/webPush')
                .then((m) => m.enableWebPush(token))
                .catch(() => {});
        }, 4000);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const token = getAuthToken();
        if (!token) return;

        // Create socket connection
        const socket = io(API_BASE_URL, {
            auth: { token },
            transports: ['websocket', 'polling'],
        });

        socketRef.current = socket;

        socket.on('connect', () => {
            // Socket connected
        });

        socket.on('notification:new', ({ notification }) => {
            // For message notifications, check if it's from the active conversation
            if (notification.type === 'message') {
                const messageSenderId = notification.metadata?.senderId || notification.senderId;

                // Skip if this message is from the currently active conversation
                if (activeConversationUserId &&
                    messageSenderId &&
                    String(messageSenderId) === String(activeConversationUserId)) {

                    // Mark as read immediately so it doesn't show up when manually fetching
                    const token = getAuthToken();
                    if (token && notification._id) {
                        fetch(`${API_BASE_URL}/api/notifications/${notification._id}/read`, {
                            method: 'PATCH',
                            headers: { Authorization: `Bearer ${token}` },
                        }).catch(err => console.error('[NotificationBell] Failed to auto-mark as read:', err));
                    }

                    return;
                }
            }

            // Check if notification already exists (update instead of duplicate)
            setNotifications((prev) => {
                const existingIndex = prev.findIndex(n => n._id === notification._id);
                if (existingIndex !== -1) {
                    // Update existing notification
                    const updated = [...prev];
                    updated[existingIndex] = notification;
                    return updated;
                } else {
                    // Add new notification
                    return [notification, ...prev];
                }
            });

            // Only increment count if it's a new notification
            setUnreadCount((prev) => {
                const exists = notifications.some(n => n._id === notification._id);
                return exists ? prev : prev + 1;
            });
        });

        // Listen for helpdesk notifications
        socket.on('helpdesk:notification', ({ notification }) => {
            // Skip if user is currently on helpdesk page
            if (activeConversationUserId === 'helpdesk') {
                // Mark as read immediately
                const token = getAuthToken();
                if (token && notification._id) {
                    fetch(`${API_BASE_URL}/api/helpdesk/notifications/${notification._id}/read`, {
                        method: 'PATCH',
                        headers: { Authorization: `Bearer ${token}` },
                    }).catch(err => console.error('[NotificationBell] Failed to auto-mark helpdesk notification as read:', err));
                }
                return;
            }
            // Add helpdesk notification to list
            setNotifications((prev) => {
                const existingIndex = prev.findIndex(n => n._id === notification._id);
                if (existingIndex !== -1) {
                    const updated = [...prev];
                    updated[existingIndex] = notification;
                    return updated;
                } else {
                    return [notification, ...prev];
                }
            });

            setUnreadCount((prev) => {
                const exists = notifications.some(n => n._id === notification._id);
                return exists ? prev : prev + 1;
            });
        });

        // Listen for notifications marked as read (e.g., when user opens helpdesk)
        socket.on('notifications-marked-read', ({ type }) => {
            console.log('[NotificationBell] Notifications marked as read:', type);
            if (type === 'helpdesk') {
                // Remove all helpdesk notifications from list
                setNotifications((prev) => prev.filter(n => n.type !== 'helpdesk_message'));
                // Refresh count
                fetchUnreadCount();
            }
        });

        socket.on('disconnect', () => {
            // Socket disconnected
        });

        socket.on('connect_error', (error) => {
            console.error('[NotificationBell] Socket connection error:', error);
        });

        // Listen for helpdesk page clearing notifications
        const handleHelpdeskCleared = () => {
            // Remove all helpdesk notifications from list
            setNotifications((prev) => prev.filter(n => n.type !== 'helpdesk_message'));
            // Refresh count
            fetchUnreadCount();
        };

        window.addEventListener('helpdesk-notifications-cleared', handleHelpdeskCleared);

        return () => {
            window.removeEventListener('helpdesk-notifications-cleared', handleHelpdeskCleared);
            socket.disconnect();
        };
    }, []);

    // Listen for notifications being marked as read from other components
    useEffect(() => {
        const handleNotificationsMarkedRead = (event) => {
            const { notificationIds } = event.detail;
            if (notificationIds && notificationIds.length > 0) {
                // Remove these notifications from the list
                setNotifications((prev) => prev.filter(n => !notificationIds.includes(n._id)));
                // Decrease unread count
                setUnreadCount((prev) => Math.max(0, prev - notificationIds.length));
            }
        };

        window.addEventListener('notifications-marked-read', handleNotificationsMarkedRead);
        return () => {
            window.removeEventListener('notifications-marked-read', handleNotificationsMarkedRead);
        };
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Fetch unread count on mount and periodically
    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000); // Every 30 seconds
        return () => clearInterval(interval);
    }, [fetchUnreadCount]);

    // Fetch notifications when dropdown opens
    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen, fetchNotifications]);

    return (
        <div className="notification-bell-container" ref={dropdownRef}>
            <button
                className="notification-bell-button"
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Notifications"
            >
                <Bell size={16} />
                {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
            </button>

            {isOpen && (
                <div className="notification-dropdown">
                    <div className="notification-header">
                        <h3>Unread Notifications</h3>
                        {unreadCount > 0 && (
                            <button className="mark-all-read" onClick={markAllAsRead} title="Mark all as read">
                                <CheckCheck size={16} />
                                <span>Mark all as read</span>
                            </button>
                        )}
                    </div>

                    <div className="notification-list">
                        {isLoading ? (
                            <div className="notification-loading">Loading...</div>
                        ) : notifications.length === 0 ? (
                            <div className="notification-empty">No unread notifications</div>
                        ) : (
                            notifications.map((notification) => (
                                <div
                                    key={notification._id}
                                    className="notification-item unread"
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="notification-icon">{getNotificationIcon(notification.type)}</div>
                                    <div className="notification-text">
                                        <div className="notification-title">{notification.title}</div>
                                        <div className="notification-message">{notification.message}</div>
                                        <div className="notification-time">{getTimeAgo(notification.createdAt)}</div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            <style jsx>{`
        .notification-bell-container {
          position: relative;
        }

        .notification-bell-button {
          position: relative;
          background: transparent;
          border: 1px solid var(--border-primary, #e5e9f1);
          cursor: pointer;
          padding: 8px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-primary, #1f2937);
          transition: background-color 0.2s;
        }

        .notification-bell-button:hover {
          background-color: var(--bg-hover, rgba(0, 0, 0, 0.05));
        }

        .notification-badge {
          position: absolute;
          top: 4px;
          right: 4px;
          background: #ef4444;
          color: white;
          font-size: 10px;
          font-weight: 600;
          padding: 2px 5px;
          border-radius: 10px;
          min-width: 18px;
          text-align: center;
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
        }

        .notification-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          width: 380px;
          max-width: 90vw;
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          overflow: hidden;
        }

        .notification-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          border-bottom: 1px solid #e5e7eb;
        }

        .notification-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
        }

        .mark-all-read {
          background: transparent;
          border: 1px solid #e5e7eb;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
          color: #6b7280;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .mark-all-read:hover {
          background: #f3f4f6;
          border-color: #d1d5db;
          color: #3b82f6;
        }

        .notification-list {
          max-height: 400px;
          overflow-y: auto;
        }

        .notification-loading,
        .notification-empty {
          padding: 40px 16px;
          text-align: center;
          color: #9ca3af;
          font-size: 14px;
        }

        .notification-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px 16px;
          border-bottom: 1px solid #f3f4f6;
          transition: background-color 0.2s;
          cursor: pointer;
        }

        .notification-item:hover {
          background-color: #f9fafb;
        }

        .notification-item.unread {
          background-color: #eff6ff;
        }

        .notification-item.unread:hover {
          background-color: #dbeafe;
        }

        .notification-icon {
          font-size: 24px;
          flex-shrink: 0;
        }

        .notification-text {
          flex: 1;
          min-width: 0;
        }

        .notification-title {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .notification-message {
          font-size: 13px;
          color: #6b7280;
          line-height: 1.4;
          margin-bottom: 4px;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        .notification-time {
          font-size: 11px;
          color: #9ca3af;
        }

        @media (max-width: 640px) {
          .notification-dropdown {
            width: calc(100vw - 32px);
            right: 0;
            border-radius: 12px;
          }
        }
      `}</style>
        </div>
    );
}

